import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";

import {
  Collapse,
  Button,
  Row,
  Col,
  Progress
} from '@lockerpm/design';

import {
  ThunderboltOutlined
} from "@ant-design/icons";

import global from "../../../../../config/global";
import common from "../../../../../utils/common";
import paymentServices from "../../../../../services/payment";

import { CipherType } from "../../../../../web-sh/src/core-js/src/enums";

const PlanInfo = (props) => {
  const { className } = props
  const { t } = useTranslation();

  const gb = 1073741824;

  const plan = useSelector((state) => state.payment.plan);
  const userInfo = useSelector((state) => state.auth.userInfo);
  const planLimit = useSelector((state) => state.payment.planLimit);

  const isFreePlan = useMemo(() => {
    return common.isFreePlan();
  }, [plan])

  const isLifeTimeUser = useMemo(() => {
    return common.isLifeTimeUser();
  }, [plan, userInfo])

  const isFamilyUser = useMemo(() => {
    return common.isFamilyUser();
  }, [plan])

  const planName = useMemo(() => {
    return common.displayPlanName();
  }, [plan])

  const [itemsStorage, setItemsStorage] = useState([])
  const [collapseKeys, setCollapseKeys] = useState(['1'])

  useEffect(() => {
    getItemsStorage();
  }, [planLimit, isFreePlan])

  const getItemsStorage = () => {
    if (planLimit?.plan_limit) {
      const limit = planLimit?.plan_limit;
      const ciphers = planLimit?.ciphers;
      const addresses = planLimit?.relay_addresses;
      const attachment = planLimit?.attachments;
      const result = [
        {
          title: 'plans_billing.info.total_items',
          limit: limit.limit_total,
          total: ciphers.total - ciphers[CipherType.TOTP],
          showProgress: isFreePlan,
        },
        {
          title: 'plans_billing.info.otps',
          limit: limit[CipherType.TOTP],
          total: ciphers[CipherType.TOTP],
          showProgress: isFreePlan,
        },
        {
          title: 'plans_billing.info.email_aliases',
          limit: limit.relay_addresses,
          total: addresses.total,
          showProgress: isFreePlan,
        },
        {
          title: 'plans_billing.info.file_attachment',
          limit: limit.limit_attachments / gb,
          total: Number((attachment?.total / gb)).toFixed(4) || 0,
          showProgress: !isFreePlan,
          hide: isFreePlan,
          unit: 'GB'
        }
      ].filter((item) => !item.hide)
      setItemsStorage(result)
    } else {
      setItemsStorage([])
    }
  }

  const handleCancelPlan = () => {
    global.confirm(async () => {
      await paymentServices.cancel_plan().then(async () => {
        global.pushSuccess(t('notification.success.payments.unsubscribed', { date: common.convertDateTime(plan?.next_billing_time, 'LL') }))
        await common.getPaymentPlan();
      }).catch((error) => {
        global.pushError(error)
      })
    }, {
      title: t('plans_billing.button'),
      content: t('plans_billing.confirm_unsubscribe', { date: common.convertDateTime(plan?.next_billing_time, 'LL') }),
      okText: t('button.ok'),
      okButtonProps: { danger: false },
    })
  }

  const CollapseHeader = () => {
    return <div className="flex items-center justify-between flex-wrap">
      <p className="font-semibold">{t('common.plan')}: {t(planName)}</p>
      {
        isFreePlan ? <Button
          type="primary"
          size="large"
          icon={<ThunderboltOutlined />}
          onClick={() => common.clickBuyPremium()}
        >
          {t('button.buy_premium')}
        </Button> : <>
          {
            (isLifeTimeUser || isFamilyUser) ? <></> : <div className="text-right flex items-center flex-wrap">
              <div className="text-sm">
                {
                  plan?.cancel_at_period_end ? t('plans_billing.cancel_at_period_end') : t('common.next_billing_date')
                }
                <span className="font-semibold">
                  : {common.convertDateTime(plan?.next_billing_time, 'LLL')}
                </span>
              </div>
              {
                plan?.next_billing_time && !plan?.cancel_at_period_end && <Button
                  className="sm:ml-4 sm:mt-0 mt-2"
                  onClick={() => handleCancelPlan()}
                >
                  { t('plans_billing.button') }
                </Button>
              }
            </div>
          }
        </>
      }
    </div>
  }

  const CollapseBody = () => {
    return <Row gutter={[16, 16]}>
      <Col lg={12} md={12} span={24}>
        <p className="font-semibold">{t('sidebar.plan_storage')}</p>
        <p className="text-black-500">{t('plans_billing.plan_storage_desc')}</p>
      </Col>
      <Col lg={12} md={12} span={24}>
        <div className="flex flex-col gap-4">
          {
            itemsStorage.map((item, index) => 
              <div key={index}>
                <div className="flex items-center justify-between">
                  <p className="">{t(item.title)}</p>
                  {
                    item.showProgress ? <p>{item.total}/{item.limit} {item.unit}</p> : <p className="md:text-right">
                      {t('common.unlimited')}
                    </p>
                  }
                </div>
                {
                  item.showProgress && <Progress
                    className='m-0'
                    status={common.getPercentageStatus(item.total / item.limit)}
                    showInfo={false}
                    percent={(item.total / item.limit) * 100}
                  />
                }
              </div>
            )
          }
        </div>
      </Col>
    </Row>
  }

  return (
    <div className={`plan-info ${className}`}>
      <Collapse
        activeKey={collapseKeys}
        expandIconPosition={'end'}
        onChange={setCollapseKeys}
      >
        <Collapse.Panel
          key="1"
          header={<CollapseHeader/>}
        >
          <CollapseBody />
        </Collapse.Panel>
      </Collapse>
    </div>
  );
}

export default PlanInfo;